import { Component, Input, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { catchError, finalize, takeUntil } from "rxjs/operators";
import { Statement } from "../statement";
import {
  PremiumPlusOfferAcceptResponse,
  PremiumPlusOfferResponse,
  StatementService
} from "app/statements/statement.service";
import { SnackbarConfig, SubscribingComponent } from "app/common";
import { SelectsDataInterface } from "../selects-data";
import { getSelectsData, State } from "app/reducers";
import { Store } from "@ngrx/store";
import { RefundDetails } from "../refund-details";
import { PackageOptions } from "../package-options";
import { SHOW_SNACKBAR } from "app/reducers/actions/snackbar";
import { ToolsBarButtonInterface } from "../../shared/components/tools-bar/tools-bar.component";
import { WorkflowControllerService } from "app/core/workflow/workflow-controller/workflow-controller.service";
import { SummaryStatementCategories } from "app/common/confirm-tax-return/ctr-categories";
import { combineLatest, of, zip } from "rxjs";
import { SocialIncomeTypes } from "app/modules/statement/models/additional-income/social-income.types";
import { FreelanceJobTypes } from "app/modules/statement/models/additional-income/freelance-job.types";
import { YousignComponent, YousignSignatureStep } from "app/shared/components/yousign-iframe/yousign.component";
import { YOUSIGN_SIGNATURE_NAME_KEYS } from "app/shared/services/yousign/yousign.http.service";
import { TranslateService } from "@ngx-translate/core";
import { environment } from "environments/environment";

type ConfirmTaxReturnView = "summary" | "upsell" | "signature";
type PremiumPlusOfferMockStatus = "none" | "offered" | "accepted" | "declined" | "completed";
type PremiumPlusOfferMockOrigin = "offer" | "manual" | null;

interface PremiumPlusOfferMockConfig {
  enabled: boolean;
  response?: Partial<PremiumPlusOfferResponse> | null;
}

@Component({
  selector: "app-confirm-tax-return",
  templateUrl: "./confirm-tax-return.component.html",
  styleUrls: ["../../common/confirm-tax-return/ctr-base.component.scss", "./confirm-tax-return.component.scss"],
  standalone: false
})
export class ConfirmTaxReturnComponent extends SubscribingComponent implements OnInit {
  @ViewChild(YousignComponent) private yousignComponent?: YousignComponent;
  @Input() statement: Statement;
  @Input() isBusiness = false;
  public summaryStatementCategories: SummaryStatementCategories;
  public summaryStatementCategoriesSpouse: SummaryStatementCategories;
  public reportError = false;
  public addAdditionalComment = false;
  public additionalCommentMsg = "";
  public incomeSummary: any = "0.00";
  public dataSelects: SelectsDataInterface;
  public refundDetails: RefundDetails;
  public taxBracket: string;
  public additionalIncomeTypes = SocialIncomeTypes;
  public buttons: ToolsBarButtonInterface[] = [];
  public premiumPlusOffer: PremiumPlusOfferResponse | null = null;
  public premiumPlusView: ConfirmTaxReturnView = "summary";
  public premiumPlusCanProceed = false;
  public premiumPlusSignatureStep: YousignSignatureStep = "single";
  private loading = false;
  private premiumPlusAccepting = false;
  private premiumPlusDeclining = false;
  private premiumPlusSignatureProceeding = false;
  private _isInitialized = false;
  private loadingStatementData = true;
  private loadingSelectsData = true;
  public statementId: number;
  private premiumPlusOfferMockState: PremiumPlusOfferResponse | null = null;

  public freelanceJobTypes: FreelanceJobTypes;

  constructor(
    private statementService: StatementService,
    private router: Router,
    private route: ActivatedRoute,
    private store: Store<State>,
    public workFlowController: WorkflowControllerService,
    private translate: TranslateService
  ) {
    super();
    this.setSummaryButtons(false);
  }

  ngOnInit(): void {
    combineLatest([this.route.params, this.store.select(getSelectsData)])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(([params, selectsData]) => {
        if (params && selectsData) {
          this.dataSelects = selectsData;
          this.loadingSelectsData = false;

          this.statementId = parseInt(params["statementId"], 10);
          this.getStatementData();
        }
      });
  }

  public get isInitialized(): boolean {
    return (
      !!this.statement &&
      this._isInitialized &&
      !this.loadingSelectsData &&
      !!this.summaryStatementCategories &&
      !!this.summaryStatementCategoriesSpouse
    );
  }

  public get isLoading(): boolean {
    return (
      this.loadingStatementData ||
      this.loading ||
      this.loadingSelectsData ||
      this.premiumPlusAccepting ||
      this.premiumPlusDeclining
    );
  }

  public get isSummaryView(): boolean {
    return this.premiumPlusView === "summary";
  }

  public get isUpsellView(): boolean {
    return this.premiumPlusView === "upsell";
  }

  public get isSignatureView(): boolean {
    return this.premiumPlusView === "signature";
  }

  public get premiumPlusOfferedAmount(): number {
    return this.parseAmount(this.premiumPlusOffer?.offered_amount);
  }

  public get premiumPlusCurrentAmount(): number {
    return this.parseAmount(this.premiumPlusOffer?.current_amount);
  }

  public get currencySymbol(): string {
    return this.dataSelects?.packageOptions?.currencySymbol?.symbol || "EUR";
  }

  public get premiumPlusSignatureTitleTranslationKey(): string {
    if (this.premiumPlusSignatureStep === "joint-spouse") {
      return "VOLLMACHTAUTHORIZATION.TITLE_PREMIUM_PLUS_JOINT_SPOUSE";
    }

    if (this.premiumPlusSignatureStep === "joint-applicant") {
      return "VOLLMACHTAUTHORIZATION.TITLE_PREMIUM_PLUS_JOINT_APPLICANT";
    }

    return "VOLLMACHTAUTHORIZATION.TITLE_PREMIUM_PLUS_SINGLE";
  }

  public get premiumPlusSignatureDescriptionTranslationKey(): string {
    if (this.premiumPlusSignatureStep === "joint-spouse") {
      return "VOLLMACHTAUTHORIZATION.DESCRIPTION_PREMIUM_PLUS_JOINT_SPOUSE";
    }

    if (this.premiumPlusSignatureStep === "joint-applicant") {
      return "VOLLMACHTAUTHORIZATION.DESCRIPTION_PREMIUM_PLUS_JOINT_APPLICANT";
    }

    return "VOLLMACHTAUTHORIZATION.DESCRIPTION_PREMIUM_PLUS_SINGLE";
  }

  public handleAdditionalCommentClick(domElement: HTMLElement): void {
    setTimeout(() => {
      domElement.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);

    this.addAdditionalComment = !this.addAdditionalComment;
  }

  public handleReportError(keyChanged, oldValue, newValue): void {
    if (!this.summaryStatementCategories || !this.summaryStatementCategoriesSpouse) {
      return;
    }
    let reportError = false;

    if (keyChanged === "reportError" && newValue) {
      reportError = true;
    }

    if (!reportError) {
      reportError = this.summaryStatementCategories.hasReportError;
    }

    if (!reportError) {
      reportError = this.summaryStatementCategoriesSpouse.hasReportError;
    }

    this.reportError = reportError;
    if (this.isSummaryView) {
      this.setSummaryButtons(reportError);
    }
  }

  public absoluteValue(number: number): number {
    return Math.abs(number);
  }

  public acceptPremiumPlusOffer(): void {
    if (this.premiumPlusAccepting || this.premiumPlusDeclining) {
      return;
    }

    this.premiumPlusAccepting = true;

    if (this.isPremiumPlusOfferMockEnabled()) {
      this.acceptPremiumPlusOfferMock();
      return;
    }

    this.tryAcceptPremiumPlusOffer(0);
  }

  public declinePremiumPlusOffer(): void {
    if (this.premiumPlusAccepting || this.premiumPlusDeclining) {
      return;
    }

    this.premiumPlusDeclining = true;

    if (this.isPremiumPlusOfferMockEnabled()) {
      this.declinePremiumPlusOfferMock();
      return;
    }

    this.statementService
      .declinePremiumPlusOffer(this.statementId)
      .pipe(
        takeUntil(this.ngUnsubscribe),
        finalize(() => {
          this.premiumPlusDeclining = false;
        })
      )
      .subscribe({
        next: () => {
          this.getStatementData();
        },
        error: (error) => {
          // The offer is no longer live (accepted elsewhere, cancelled, ...): reload the actual state.
          if (this.extractErrorDetail(error) === "no_offer_to_decline") {
            this.getStatementData();
            return;
          }

          this.showGlobalErrorSnackbar();
        }
      });
  }

  public onPremiumPlusSignatureStepChange(step: YousignSignatureStep): void {
    this.premiumPlusSignatureStep = step;
  }

  public onPremiumPlusVerificationChange(isVerified: boolean): void {
    this.premiumPlusCanProceed = isVerified;

    if (isVerified && this.premiumPlusSignatureStep !== "joint-applicant" && !this.isPremiumPlusOfferMockEnabled()) {
      this.completePremiumPlusSignatureFlow();
    }
  }

  public handlePremiumPlusProceed(): void {
    if (this.premiumPlusSignatureStep === "joint-applicant") {
      this.premiumPlusCanProceed = false;
      this.yousignComponent?.goToSpouseSignatureStep();
      return;
    }

    this.completePremiumPlusSignatureFlow();
  }

  private getStatementData(): void {
    this.loadingStatementData = true;

    const premiumPlusOfferRequest$ = this.isPremiumPlusOfferMockEnabled()
      ? of(this.getPremiumPlusMockState())
      : this.statementService.getPremiumPlusOffer(this.statementId).pipe(catchError(() => of(null)));

    zip(
      this.statementService.getStatement(this.statementId),
      this.statementService.getRefundDetails(this.statementId),
      premiumPlusOfferRequest$
    )
      .pipe(
        takeUntil(this.ngUnsubscribe),
        finalize(() => {
          this.loadingStatementData = false;
          this.premiumPlusSignatureProceeding = false;
        })
      )
      .subscribe({
        next: ([responseStatement, responseRefundDetails, responseOffer]) => {
          this.statement = responseStatement;

          this.refundDetails = responseRefundDetails;

          this.initSummaryCategories();

          this.workFlowController.init(this.statement);
          if (!this.statement.isForClientApproval || this.statement.serviceType !== PackageOptions.tax_adviser) {
            this.workFlowController.goToTheDashboard();
            return;
          }

          this.resolvePremiumPlusView(this.resolvePremiumPlusOfferResponse(responseOffer));
          this._isInitialized = true;
        },
        error: () => {
          this.router.navigate(["/new"]);
        }
      });
  }

  private updateStatementAndGoToNextStep(hasAcceptedNewPrice: boolean | null = null): void {
    const data = this.summaryStatementCategories.categoryPropertiesList;
    const dataSpouse = this.summaryStatementCategoriesSpouse.categoryPropertiesList;

    this.loading = true;
    this.statementService
      .confirmStatementOrReportErrors(
        this.statement.id,
        this.statement.confirmStatementToResponse(
          data,
          dataSpouse,
          this.addAdditionalComment ? this.additionalCommentMsg : "",
          this.statement.request_new_price_acceptance,
          hasAcceptedNewPrice
        )
      )
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (r: { success: boolean }) => {
          if (r.success) {
            this.goToNextStep();
          } else {
            this.loading = false;
            this.store.dispatch({
              type: SHOW_SNACKBAR,
              payload: new SnackbarConfig("COMMON.GLOBAL_ERROR", "ERROR")
            });
          }
        },
        error: (error: any) => {
          if (this.extractErrorDetail(error) === "payment_instruction_signature_pending") {
            this.loading = false;
            this.getStatementData();
            return;
          }

          console.warn(error, "ERROR");
          this.loading = false;
          this.showGlobalErrorSnackbar();
        }
      });
  }

  private goToNextStep(): void {
    this.workFlowController.goToTheDashboard();
  }

  private proceed(): void {
    this.updateStatementAndGoToNextStep();
  }

  private initSummaryCategories(): void {
    this.summaryStatementCategories = new SummaryStatementCategories(
      this.handleReportError.bind(this),
      this.refundDetails,
      this.statement,
      this.statement
    );
    this.summaryStatementCategoriesSpouse = new SummaryStatementCategories(
      this.handleReportError.bind(this),
      this.refundDetails,
      this.statement,
      this.statement.spouse
    );
  }

  private resolvePremiumPlusView(offer: PremiumPlusOfferResponse | null): void {
    this.premiumPlusOffer = offer;

    if (!offer || offer.status === "none" || offer.status === "declined" || offer.status === "completed") {
      this.setView("summary");
      return;
    }

    if (!offer.payment_instruction_signed && (offer.status === "accepted" || offer.origin === "manual")) {
      this.premiumPlusCanProceed = false;
      this.premiumPlusSignatureStep = offer.sign_with_spouse ? "joint-applicant" : "single";
      this.setView("signature");
      return;
    }

    if (offer.status === "offered") {
      this.premiumPlusCanProceed = false;
      this.premiumPlusSignatureStep = "single";
      this.setView("upsell");
      return;
    }

    this.setView("summary");
  }

  private setView(view: ConfirmTaxReturnView): void {
    this.premiumPlusView = view;

    if (view === "summary") {
      this.setSummaryButtons(this.reportError);
      return;
    }

    if (view === "upsell") {
      this.setUpsellButtons();
      return;
    }

    this.setSignatureButtons();
  }

  private setSummaryButtons(reportError: boolean): void {
    if (reportError) {
      this.buttons = [
        {
          type: "danger",
          label: "COMMON.REPORT_ERRORS",
          action: this.proceed.bind(this)
        }
      ];
      return;
    }

    this.buttons = [
      {
        type: "proceed",
        label: "COMMON.CONFIRM",
        action: this.proceed.bind(this)
      }
    ];
  }

  private setUpsellButtons(): void {
    this.buttons = [
      {
        type: "standard",
        label: "COMMON.DECLINE",
        action: this.declinePremiumPlusOffer.bind(this),
        disabled: () => this.premiumPlusAccepting || this.premiumPlusDeclining
      },
      {
        type: "proceed",
        label: "COMMON.ACCEPT",
        action: this.acceptPremiumPlusOffer.bind(this),
        disabled: () => this.premiumPlusAccepting || this.premiumPlusDeclining
      }
    ];
  }

  private setSignatureButtons(): void {
    this.buttons = [
      {
        type: "proceed",
        label: "COMMON.PROCEED",
        action: this.handlePremiumPlusProceed.bind(this),
        disabled: () => !this.premiumPlusCanProceed || this.premiumPlusSignatureProceeding
      }
    ];
  }

  private tryAcceptPremiumPlusOffer(attempt: number): void {
    this.statementService
      .acceptPremiumPlusOffer(
        this.statementId,
        this.translate.instant(YOUSIGN_SIGNATURE_NAME_KEYS["payment-instruction"])
      )
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (response: PremiumPlusOfferAcceptResponse) => {
          this.premiumPlusAccepting = false;
          this.premiumPlusCanProceed = false;
          this.premiumPlusSignatureStep = response.sign_with_spouse ? "joint-applicant" : "single";

          this.premiumPlusOffer = {
            ...(this.premiumPlusOffer || {
              status: "accepted",
              origin: "offer",
              offered_amount: "0.00",
              offered_min_amount: "0.00",
              current_amount: "0.00",
              is_percentage_payment: true,
              payment_instruction_signed: false,
              sign_with_spouse: response.sign_with_spouse
            }),
            status: "accepted",
            sign_with_spouse: response.sign_with_spouse,
            payment_instruction_signed: false
          };

          this.setView("signature");
        },
        error: (error) => {
          const detail = this.extractErrorDetail(error);

          if (detail === "retry" && attempt < 2) {
            this.tryAcceptPremiumPlusOffer(attempt + 1);
            return;
          }

          this.premiumPlusAccepting = false;

          if (detail === "signing_unavailable") {
            this.premiumPlusCanProceed = false;
            this.setView("signature");
            return;
          }

          if (detail === "no_longer_eligible") {
            // The backend keeps the row "offered", so a reload would show the upsell again.
            // Continue on the flat price instead; confirming silently retires the offer.
            this.setView("summary");
            this.store.dispatch({
              type: SHOW_SNACKBAR,
              payload: new SnackbarConfig("CONFIRM_TAX_RETURN.PREMIUM_PLUS_OFFER.NO_LONGER_ELIGIBLE", "INFO", 8000)
            });
            return;
          }

          if (detail === "no_offer_to_accept") {
            this.getStatementData();
            return;
          }

          this.showGlobalErrorSnackbar();
        }
      });
  }

  private completePremiumPlusSignatureFlow(): void {
    if (this.premiumPlusSignatureProceeding) {
      return;
    }

    this.premiumPlusSignatureProceeding = true;

    if (this.isPremiumPlusOfferMockEnabled()) {
      this.completePremiumPlusSignatureFlowMock();
      return;
    }

    this.getStatementData();
  }

  private resolvePremiumPlusOfferResponse(offer: PremiumPlusOfferResponse | null): PremiumPlusOfferResponse | null {
    if (!this.isPremiumPlusOfferMockEnabled()) {
      return offer;
    }

    if (offer) {
      this.premiumPlusOfferMockState = offer;
      return offer;
    }

    return this.getPremiumPlusMockState();
  }

  private acceptPremiumPlusOfferMock(): void {
    const currentMockOffer = this.getPremiumPlusMockState();
    this.premiumPlusOfferMockState = {
      ...currentMockOffer,
      status: "accepted",
      origin: currentMockOffer.origin || "offer",
      is_percentage_payment: true,
      payment_instruction_signed: false
    };
    this.premiumPlusOffer = { ...this.premiumPlusOfferMockState };
    this.premiumPlusAccepting = false;
    this.premiumPlusCanProceed = false;
    this.premiumPlusSignatureStep = this.premiumPlusOffer.sign_with_spouse ? "joint-applicant" : "single";
    this.setView("signature");
  }

  private declinePremiumPlusOfferMock(): void {
    const currentMockOffer = this.getPremiumPlusMockState();
    this.premiumPlusOfferMockState = {
      ...currentMockOffer,
      status: "declined"
    };
    this.premiumPlusOffer = { ...this.premiumPlusOfferMockState };
    this.premiumPlusDeclining = false;
    this.resolvePremiumPlusView(this.premiumPlusOffer);
  }

  private completePremiumPlusSignatureFlowMock(): void {
    const currentMockOffer = this.getPremiumPlusMockState();
    this.premiumPlusOfferMockState = {
      ...currentMockOffer,
      status: "completed",
      payment_instruction_signed: true,
      is_percentage_payment: true,
      current_amount: currentMockOffer.offered_amount || currentMockOffer.current_amount
    };
    this.premiumPlusSignatureProceeding = false;
    this.resolvePremiumPlusView({ ...this.premiumPlusOfferMockState });
  }

  private isPremiumPlusOfferMockEnabled(): boolean {
    return !environment.production && !!this.getPremiumPlusOfferMockConfig()?.enabled;
  }

  private getPremiumPlusOfferMockConfig(): PremiumPlusOfferMockConfig | null {
    return ((environment as any).mockPremiumPlusOffer as PremiumPlusOfferMockConfig) || null;
  }

  private getPremiumPlusMockState(): PremiumPlusOfferResponse {
    if (this.premiumPlusOfferMockState) {
      return { ...this.premiumPlusOfferMockState };
    }

    const config = this.getPremiumPlusOfferMockConfig();
    const response = config?.response || {};
    this.premiumPlusOfferMockState = {
      status: this.normalizePremiumPlusMockStatus(response.status),
      origin: this.normalizePremiumPlusMockOrigin(response.origin),
      offered_amount: this.normalizePremiumPlusMockAmount(response.offered_amount, "0.00"),
      offered_min_amount: this.normalizePremiumPlusMockAmount(response.offered_min_amount, "0.00"),
      current_amount: this.normalizePremiumPlusMockAmount(response.current_amount, "99.00"),
      is_percentage_payment: !!response.is_percentage_payment,
      payment_instruction_signed: !!response.payment_instruction_signed,
      sign_with_spouse: !!response.sign_with_spouse
    };

    return { ...this.premiumPlusOfferMockState };
  }

  private normalizePremiumPlusMockStatus(value: any): PremiumPlusOfferMockStatus {
    switch (value) {
      case "none":
      case "offered":
      case "accepted":
      case "declined":
      case "completed":
        return value;
      default:
        return "offered";
    }
  }

  private normalizePremiumPlusMockOrigin(value: any): PremiumPlusOfferMockOrigin {
    return value === "offer" || value === "manual" ? value : null;
  }

  private normalizePremiumPlusMockAmount(value: any, fallback: string): string {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed.toFixed(2) : fallback;
  }

  private parseAmount(value: string | number | undefined | null): number {
    if (typeof value === "number") {
      return value;
    }

    if (!value) {
      return 0;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private extractErrorDetail(error: any): string | null {
    return error?.error?.detail || error?.detail || null;
  }

  private showGlobalErrorSnackbar(): void {
    this.store.dispatch({
      type: SHOW_SNACKBAR,
      payload: new SnackbarConfig("COMMON.GLOBAL_ERROR", "ERROR")
    });
  }
}
